function applyCaseInsensitivePrefixRule(word, prefixMap, capitalizeFirst = false) {
    const lowerWord = word.toLowerCase();
    for (const [prefix, replacement] of Object.entries(prefixMap)) {
        if (lowerWord.startsWith(prefix)) {
            const rest = word.slice(prefix.length);
            return capitalizeFirst ? replacement.charAt(0).toUpperCase() + replacement.slice(1) + rest : replacement + rest;
        }
    }
    return word;
}
function applyToHyphenatedParts(word, processingFunction) {
    if (word.includes("-")) {
        return word.split("-").map(processingFunction).join("-");
    }
    return processingFunction(word);
}
function applyToWords(name, wordProcessingFunction) {
    return name.split(" ").map(wordProcessingFunction).join(" ");
}
const basicCapitalizationRule = {
    name: "Basic Capitalization",
    apply: (name) => {
        const capitalizeWord = (word) => word.charAt(0).toUpperCase() + word.slice(1);
        return applyToWords(name, (word) => applyToHyphenatedParts(word, capitalizeWord));
    },
};
const celticPrefixRule = {
    name: "Celtic Prefixes",
    apply: (name) => {
        const processCelticPart = (part) => {
            const lowerPart = part.toLowerCase();
            const celticPrefixes = [
                { prefix: "mc", replacement: "Mc" },
                { prefix: "mac", replacement: "Mac" },
                { prefix: "o'", replacement: "O'" },
            ];
            for (const { prefix, replacement } of celticPrefixes) {
                if (lowerPart.startsWith(prefix)) {
                    return `${replacement}${part.slice(prefix.length).charAt(0).toUpperCase()}${part.slice(prefix.length + 1)}`;
                }
            }
            return part;
        };
        return applyToWords(name, (word) => applyToHyphenatedParts(word, processCelticPart));
    },
};
const nobilityParticleRule = {
    name: "Nobility Particles",
    apply: (name) => {
        const particles = [
            "van",
            "de",
            "der",
            "den",
            "te",
            "'t",
            "von",
            "zu",
            "zum",
            "zur",
            "de",
            "du",
            "des",
            "le",
            "la",
            "les",
            "di",
            "da",
            "del",
            "degli",
            "delle",
        ];
        const compounds = [
            "van der",
            "van den",
            "van de",
            "de la",
            "de le",
            "von der",
            "zu der",
            "de la",
            "de los",
        ];
        let updatedName = name;
        for (const compound of compounds) {
            const pattern = new RegExp(`\\b${compound}\\b`, "gi");
            updatedName = updatedName.replace(pattern, compound);
        }
        return applyToWords(name, (word, index, array) => {
            if (index === 0 || index === array.length - 1)
                return word;
            const lowerWord = word.toLowerCase();
            return particles.includes(lowerWord) ? lowerWord : word;
        });
    },
};
const iberianRule = {
    name: "Iberian Names",
    apply: (name) => {
        const prefixes = {
            san: "San",
            santa: "Santa",
            são: "São",
        };
        const conjunctions = ["y", "e", "i"];
        return applyToWords(name, (word, index, array) => {
            const result = applyCaseInsensitivePrefixRule(word, prefixes, true);
            if (result !== word)
                return result;
            const lowerWord = word.toLowerCase();
            if (index > 0 && index < array.length - 1 && conjunctions.includes(lowerWord)) {
                return lowerWord;
            }
            return word;
        });
    },
};
const welshPatronymicRule = {
    name: "Welsh Patronymics",
    apply: (name) => {
        const prefixes = {
            ap: "ap",
            ab: "ab",
            ferch: "Ferch",
            verch: "Verch",
        };
        return applyToWords(name, (word, index) => {
            const capitalize = index === 0;
            return applyCaseInsensitivePrefixRule(word, prefixes, capitalize);
        });
    },
};
const arabicRule = {
    name: "Simple Arabic",
    apply: (name) => {
        const prefixes = {
            bint: "bint",
            ibn: "ibn",
        };
        return applyToWords(name, (word, index) => {
            const capitalize = index === 0;
            return applyCaseInsensitivePrefixRule(word, prefixes, capitalize);
        });
    },
};
const rules = [basicCapitalizationRule, arabicRule, celticPrefixRule, nobilityParticleRule, iberianRule, welshPatronymicRule];
export function formatName(name, debug = false) {
    if (!name)
        return name;
    let result = name.toLowerCase().trim();
    if (debug) {
        console.log(`Initial: "${result}"`);
    }
    for (const rule of rules) {
        const before = result;
        result = rule.apply(result);
        if (debug && before !== result) {
            console.log(`After ${rule.name}: "${result}"`);
        }
    }
    return result;
}
export function normalizeSignatureName(name) {
    return name
        .normalize("NFC")
        .replace(/[\u2018\u2019\u201A\u201B\u0060\u00B4]/g, "'")
        .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-")
        .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, " ")
        .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, "")
        .trim()
        .replace(/\s+/g, " ");
}
export function runNameFormatterTests() {
    const testCases = [
        {
            input: "JUAN CARLOS DE LA ROSA Y MARTINEZ",
            expected: "Juan Carlos de la Rosa y Martinez",
            description: "Spanish name with conjunction",
        },
        {
            input: "FATIMA BINT MUHAMMAD AL-RASHID",
            expected: "Fatima bint Muhammad Al-Rashid",
            description: "Arabic name with Bint and Al- prefix",
        },
        {
            input: "MUHAMMAD IBN AL-RASHID",
            expected: "Muhammad ibn Al-Rashid",
            description: "Arabic name with Ibn and Al- prefix",
        },
        {
            input: "RHYS AP THOMAS",
            expected: "Rhys ap Thomas",
            description: "Welsh name with patronymic",
        },
        {
            input: "SEAMUS O'CONNOR",
            expected: "Seamus O'Connor",
            description: "Irish name with O' prefix",
        },
        {
            input: "SEAMUS FITZGERALD",
            expected: "Seamus Fitzgerald",
            description: "Irish name with Fitz prefix",
        },
        {
            input: "GIUSEPPE DEGLI ANGELI",
            expected: "Giuseppe degli Angeli",
            description: "Italian name with compound particle",
        },
        {
            input: "JOHN MCDONALD",
            expected: "John McDonald",
            description: "Scottish name with Mc prefix",
        },
        {
            input: "MARY-JANE VON DER SCHMIDT",
            expected: "Mary-Jane von der Schmidt",
            description: "German name with nobility particle and hyphenated first name",
        },
        {
            input: "ABDUL RAHMAN AL-RASHID",
            expected: "Abdul Rahman Al-Rashid",
            description: "Arabic name with compound and Al- prefix",
        },
        {
            input: "LEONARDO DA VINCI",
            expected: "Leonardo da Vinci",
            description: "Italian name with particle",
        },
        {
            input: "SIMONE DE BEAUVOIR",
            expected: "Simone de Beauvoir",
            description: "French name with particle",
        },
        {
            input: "ALI AL-RASHID",
            expected: "Ali Al-Rashid",
            description: "Arabic name handling",
        },
        {
            input: "SAN MIGUEL DE ALLENDE",
            expected: "San Miguel de Allende",
            description: "Spanish/Portuguese name handling",
        },
        {
            input: "IAN MCGIBBONS-O'CONNOR",
            expected: "Ian McGibbons-O'Connor",
            description: "Hyphenated Celtic name",
        },
        {
            input: "IAN O'CONNOR-MCGIBBONS",
            expected: "Ian O'Connor-McGibbons",
            description: "Hyphenated Celtic name part 2",
        },
    ];
    console.log("Running name formatter tests...\n");
    let passCount = 0;
    let failCount = 0;
    for (const { input, expected, description } of testCases) {
        const actual = formatName(input, true);
        const passed = actual === expected;
        if (passed) {
            passCount++;
            console.log(`✓ PASS: ${description}`);
            console.log(`  Input:    "${input}"`);
            console.log(`  Expected: "${expected}"`);
        }
        else {
            failCount++;
            console.log(`✗ FAIL: ${description}`);
            console.log(`  Input:    "${input}"`);
            console.log(`  Expected: "${expected}"`);
            console.log(`  Actual:   "${actual}"\n`);
        }
    }
    console.log(`Test Summary:`);
    console.log(`  Total:  ${testCases.length}`);
    console.log(`  Passed: ${passCount}`);
    console.log(`  Failed: ${failCount}`);
}
